import { Controller } from '@hotwired/stimulus';

const STATUS_LABEL = {
    delivered: 'Delivered',
    failed: 'Failed',
    pending: 'Pending',
    retrying: 'Retrying',
    read: 'Delivered', // read receipts not used; display as Delivered
};

export default class extends Controller {
    static targets = [
        'broadcastLabel',
        'statusIndicator',
        'statusDot',
        'statusLabel',
        'queuedNode',
        'lineAfterQueued',
        'gatheringNode',
        'lineAfterGathering',
        'sendingNode',
        'reachHeading',
        'sentHeading',
        'sentPercentText',
        'completeHeading',
        'completionBar',
        'failuresHeading',
        'failuresPercentText',
        'sendingPercentText',
        'completedNode',
        'finalOverviewLine',
        'recipientsSearch',
        'recipientsStatusFilter',
        'recipientsRange',
        'pageInfo',
        'pagePrev',
        'pageNext',
        'recipientsTable',
        'deliveryBarDelivered',
        'deliveryBarFailed',
        'deliveryBarRetrying',
        'legendDelivered',
        'legendPending',
        'legendRetrying',
        'legendFailed',
    ];

    static values = {
        broadcast: Object,
        metrics: Object,
        message: Object,
        config: Object,
        indexUrl: String,
        recipientsUrl: String,
        statsUrl: String,
    };

    connect() {
        this.broadcast = this.broadcastValue || {};
        this.liveMetrics = this.metricsValue || {
            reach: 0,
            sent: 0,
            delivered: 0,
            failures: 0,
        };
        this.message = this.messageValue || {};
        this.config = this.configValue || {};

        this.status = (this.broadcast.status || 'finished').toString();
        this.currentBroadcastId = Number(this.broadcast.id || 0);
        this.recipients = [];
        this.totalRecipients = 0;
        this.pageSize = 25;
        this.page = 1;
        this.searchQuery = '';
        this.recipientsStatusFilter = '';
        this.statusCounts = { delivered: 0, read: 0, pending: 0, retrying: 0, failed: 0 };
        this.recipientsLoading = false;
        this.searchDebounceTimer = null;

        this.handleRecipientsSearch = (event) => {
            const query = (event.detail || '').toString().trim();
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => this.searchRecipients(query), 300);
        };
        this.handleRecipientsClear = () => {
            clearTimeout(this.searchDebounceTimer);
            this.searchRecipients('');
        };
        this.recipientsSearchTarget.addEventListener('valueChange', this.handleRecipientsSearch);
        this.recipientsSearchTarget.addEventListener('clear', this.handleRecipientsClear);

        this.renderStatus();
        this.renderMetrics();
        this.renderRecipients();
        this.renderDeliveryBreakdown();

        this.fetchRecipients();

        if (this.status === 'running' || this.status === 'gathering' || this.status === 'queued') {
            this.startLivePolling();
        }
    }

    disconnect() {
        this.stopLivePolling();
        clearTimeout(this.searchDebounceTimer);
        this.recipientsSearchTarget.removeEventListener('valueChange', this.handleRecipientsSearch);
        this.recipientsSearchTarget.removeEventListener('clear', this.handleRecipientsClear);
    }

    onRecipientsStatusChange(event) {
        const id = (event?.detail || 'all').toString();
        this.recipientsStatusFilter = id === 'all' ? '' : id;
        this.page = 1;
        this.fetchRecipients();
    }

    startLivePolling() {
        const pollInterval = 2000; // 2 seconds

        const poll = async () => {
            if (this.status === 'finished' || this.status === 'failed') {
                return;
            }
            const url = this.statsUrlValue;
            if (!url) return;

            try {
                const res = await fetch(url, { headers: { Accept: 'application/json' } });
                if (!res.ok) return;
                const data = await res.json();

                this.status = data.status || this.status;
                if (data.metrics) {
                    this.liveMetrics = {
                        reach: data.metrics.reach ?? this.liveMetrics.reach,
                        sent: data.metrics.sent ?? this.liveMetrics.sent,
                        delivered: data.metrics.delivered ?? this.liveMetrics.delivered,
                        failures: data.metrics.failures ?? this.liveMetrics.failures,
                    };
                }
                if (data.status_counts) {
                    this.statusCounts = { ...this.statusCounts, ...data.status_counts };
                }

                this.renderStatus();
                this.renderMetrics();
                this.renderDeliveryBreakdown();

                if (this.status === 'finished' || this.status === 'failed') {
                    this.stopLivePolling();
                    this.fetchRecipients(); // Final refresh of recipients table
                }
            } catch {
                // Ignore fetch errors; will retry on next poll
            }
        };

        poll(); // Initial poll immediately
        this.interval = window.setInterval(poll, pollInterval);

        // Refresh recipients table every 8s during active send (lighter than stats poll)
        this.recipientsInterval = window.setInterval(() => {
            if (this.status !== 'finished' && this.status !== 'failed') {
                this.fetchRecipients();
            }
        }, 8000);
    }

    stopLivePolling() {
        if (this.interval) {
            window.clearInterval(this.interval);
            this.interval = null;
        }
        if (this.recipientsInterval) {
            window.clearInterval(this.recipientsInterval);
            this.recipientsInterval = null;
        }
    }

    renderStatus() {
        const s = this.status || 'finished';
        const isActive = s === 'running' || s === 'gathering';
        const displayLabel = s === 'gathering' ? 'Gathering list of people' : (s === 'running' ? 'Live' : this.capitalize(this.status));
        const dotMod = s === 'running' ? 'running' : s;

        this.broadcastLabelTarget.textContent = this.broadcast.title || '';
        this.statusLabelTarget.textContent = displayLabel;

        this.statusDotTarget.className = 'bcast-show__dot bcast-show__dot--' + dotMod;
        if (isActive) {
            this.statusDotTarget.classList.add('bcast-show__dot--blink');
        } else {
            this.statusDotTarget.classList.remove('bcast-show__dot--blink');
        }

        const sentPercentage = this.percentage(this.liveMetrics.sent, this.liveMetrics.reach);
        this.sendingPercentTextTarget.textContent = `${sentPercentage}%`;

        const queuedDone = s !== 'queued';
        const gatheringDone = s === 'running' || s === 'finished' || s === 'failed';
        const sendingDone = s === 'finished' || s === 'failed';
        const isFinished = s === 'finished' || s === 'failed';

        this.queuedNodeTarget.classList.toggle('broadcasting-v2__overview-node--done', queuedDone);
        this.queuedNodeTarget.classList.toggle('broadcasting-v2__overview-node--active', s === 'queued');
        this.lineAfterQueuedTarget.classList.toggle('broadcasting-v2__overview-line--done', queuedDone);

        this.gatheringNodeTarget.classList.toggle('broadcasting-v2__overview-node--done', gatheringDone);
        this.gatheringNodeTarget.classList.toggle('broadcasting-v2__overview-node--active', s === 'gathering');
        this.lineAfterGatheringTarget.classList.toggle('broadcasting-v2__overview-line--done', gatheringDone);

        this.sendingNodeTarget.classList.toggle('broadcasting-v2__overview-node--done', sendingDone);
        this.sendingNodeTarget.classList.toggle('broadcasting-v2__overview-node--active', s === 'running');

        this.completedNodeTarget.classList.toggle('broadcasting-v2__overview-node--done', isFinished);
        this.completedNodeTarget.classList.toggle('broadcasting-v2__overview-node--active', isFinished);
        this.finalOverviewLineTarget.classList.toggle('broadcasting-v2__overview-line--done', isFinished);
    }

    renderMetrics() {
        const reach = this.liveMetrics.reach || 0;
        const sent = this.liveMetrics.sent || 0;
        const delivered = this.liveMetrics.delivered || 0;
        const failures = this.liveMetrics.failures || 0;
        const completionPct = sent > 0 ? (delivered / sent) * 100 : 0;
        const completionLabel = this.formatDeliveryRate(completionPct, failures);
        const completionBarPct = Math.min(100, Math.round(completionPct));

        this.reachHeadingTarget.text = this.formatNumber(reach);
        this.sentHeadingTarget.text = this.formatNumber(sent);
        this.completeHeadingTarget.text = `${completionLabel}%`;
        this.failuresHeadingTarget.text = this.formatNumber(failures);
        this.completionBarTarget.style.width = `${completionBarPct}%`;

        this.sentPercentTextTarget.textContent = `${this.percentage(sent, reach)}% of reach`;
        this.failuresPercentTextTarget.textContent = `${this.percentage(failures, sent)}% of sent`;
    }

    formatDeliveryRate(pct, failures) {
        if (!Number.isFinite(pct)) return '0';
        if (failures > 0 || pct % 1 !== 0) {
            return pct.toFixed(1);
        }
        return String(Math.round(pct));
    }

    renderDeliveryBreakdown() {
        const counts = this.statusCounts;
        const deliveredTotal = counts.delivered + counts.read;
        const retrying = counts.retrying || 0;
        const total = deliveredTotal + counts.pending + retrying + counts.failed;
        if (total === 0) {
            this.deliveryBarDeliveredTarget.style.width = '0%';
            this.deliveryBarRetryingTarget.style.width = '0%';
            this.deliveryBarFailedTarget.style.width = '0%';
            this.legendDeliveredTarget.textContent = 'Delivered (0)';
            this.legendPendingTarget.textContent = 'Pending (0)';
            this.legendRetryingTarget.textContent = 'Retrying (0)';
            this.legendFailedTarget.textContent = 'Failed (0)';
            return;
        }

        const pct = (n) => `${Math.round((n / total) * 100)}%`;

        this.deliveryBarDeliveredTarget.style.width = pct(deliveredTotal);
        this.deliveryBarRetryingTarget.style.width = pct(retrying);
        this.deliveryBarFailedTarget.style.width = pct(counts.failed);

        this.legendDeliveredTarget.textContent = `Delivered (${this.formatNumber(deliveredTotal)})`;
        this.legendPendingTarget.textContent = `Pending (${this.formatNumber(counts.pending)})`;
        this.legendRetryingTarget.textContent = `Retrying (${this.formatNumber(retrying)})`;
        this.legendFailedTarget.textContent = `Failed (${this.formatNumber(counts.failed)})`;
    }

    searchRecipients(query) {
        this.searchQuery = query;
        this.page = 1;
        this.fetchRecipients();
    }

    prevPage() {
        if (this.page <= 1) return;
        this.page -= 1;
        this.fetchRecipients();
    }

    nextPage() {
        const totalPages = Math.max(1, Math.ceil(this.totalRecipients / this.pageSize));
        if (this.page >= totalPages) return;
        this.page += 1;
        this.fetchRecipients();
    }

    async fetchRecipients() {
        const url = this.recipientsUrlValue;
        if (!url) return;

        this.recipientsLoading = true;
        const params = new URLSearchParams({
            page: String(this.page),
            per_page: String(this.pageSize),
        });
        if (this.searchQuery) {
            params.set('search', this.searchQuery);
        }
        if (this.recipientsStatusFilter) {
            params.set('status', this.recipientsStatusFilter);
        }

        try {
            const res = await fetch(`${url}?${params.toString()}`, {
                headers: { Accept: 'application/json' },
            });
            if (!res.ok) throw new Error('Failed to load recipients');
            const data = await res.json();

            this.recipients = data.recipients || [];
            this.totalRecipients = data.total || 0;
            this.page = data.page || 1;
            this.statusCounts = data.status_counts || { delivered: 0, read: 0, pending: 0, failed: 0 };

            this.renderRecipients();
            this.renderDeliveryBreakdown();
        } catch (err) {
            this.recipients = [];
            this.totalRecipients = 0;
            this.renderRecipients();
            this.renderDeliveryBreakdown();
        } finally {
            this.recipientsLoading = false;
        }
    }

    renderRecipients() {
        const total = this.totalRecipients;
        const totalPages = Math.max(1, Math.ceil(total / this.pageSize));
        const start = total === 0 ? 0 : (this.page - 1) * this.pageSize + 1;
        const end = Math.min(this.page * this.pageSize, total);
        const pageRecipients = this.recipients;

        const rows = pageRecipients.map((recipient) => {
            const displayStatus = recipient.status === 'read' ? 'delivered' : recipient.status;
            const statusClass = `bcast-status bcast-status--${displayStatus}`;
            const statusText = STATUS_LABEL[recipient.status] || this.capitalize(recipient.status);
            const delivered = recipient.status === 'delivered' || recipient.status === 'read';
            const errorReason = recipient.errorReason ? this.escapeHtml(recipient.errorReason) : '';

            const deliveredHtml = delivered
                ? `<span class="bcast-icon bcast-icon--yes"><svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 6.5 5.5 10 11 4" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg></span>`
                : `<span class="bcast-icon bcast-icon--no"><svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M3 3l7 7M10 3l-7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></span>`;

            return `
                <tr>
                    <td class="bcast-table__cell--name">${this.escapeHtml(recipient.name)}</td>
                    <td class="bcast-table__cell--id">${this.escapeHtml(recipient.id)}</td>
                    <td><span class="${statusClass}">${statusText}</span></td>
                    <td class="bcast-table__cell--icon">${deliveredHtml}</td>
                    <td class="bcast-table__cell--error">${errorReason}</td>
                </tr>
            `;
        });

        this.recipientsRangeTarget.textContent = total === 0 ? '0 results' : `${this.formatNumber(start)}–${this.formatNumber(end)} of ${this.formatNumber(total)}`;
        this.pageInfoTarget.textContent = `${this.page} / ${totalPages}`;
        this.pagePrevTarget.disabled = this.page <= 1;
        this.pageNextTarget.disabled = this.page >= totalPages;

        this.recipientsTableTarget.innerHTML = `
            <table class="broadcasting-v2__table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>ID</th>
                        <th>Status</th>
                        <th>Delivered</th>
                        <th>Error</th>
                    </tr>
                </thead>
                <tbody>
                    ${rows.join('') || '<tr><td colspan="5" class="bcast-table__empty">No recipients found</td></tr>'}
                </tbody>
            </table>
        `;
    }

    percentage(value, total) {
        if (!total) {
            return 0;
        }

        return Math.round((Number(value || 0) / Number(total || 1)) * 100);
    }

    capitalize(value) {
        if (!value) {
            return '';
        }

        return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
    }

    formatNumber(value) {
        const parsed = Number(value || 0);
        return Number.isFinite(parsed) ? parsed.toLocaleString() : '0';
    }

    escapeHtml(value) {
        return value
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#39;');
    }
}
